/**
 * External Access Store (REQ-AUTH-079)
 *
 * Holds the External access list for the organization currently selected in the
 * portal — the outside accounts, owned by other organizations, that hold a
 * platform grant belonging to this one.
 *
 * Which organization that is stays owned elsewhere — the staff-picked target on
 * the users store, falling back to the caller's own active org — the single
 * source of truth for every member operation's scope. This store reads it
 * through a getter rather than keeping a second copy, for the same reason
 * useOrgsStore does not keep one.
 *
 * @module stores/useExternalAccessStore
 */

import { create, type StateCreator } from 'zustand';
import type { ExternalAccessApiClient } from '../api/ExternalAccessApiClient';
import { externalAccessClient as defaultExternalAccessClient } from '../api/registry';
import type { ExternalAccessGrant, UserRole } from '../types';
import { formatErrorMessage } from '../utils';
import { useUsersStore } from './useUsersStore';
import { useAuthStore } from './useAuthStore';

export interface ExternalAccessState {
  items: ExternalAccessGrant[];
  /** The backend scanned only part of the organization's authorizations. */
  truncated: boolean;
  /** Whether this caller may grant, revoke and change roles here — staff only (D1). */
  canManage: boolean;
  isLoading: boolean;
  error: Error | null;

  load: () => Promise<void>;
  /** Resolves only when the service accepted the grant; rejects on a refusal. */
  grant: (userId: string, role: UserRole) => Promise<void>;
  /** Resolves only when the service accepted the revoke; rejects on a refusal. */
  revoke: (userId: string) => Promise<void>;
  /** Resolves only when the service accepted the role change; rejects on a refusal. */
  setRole: (userId: string, role: UserRole) => Promise<void>;
  clearError: () => void;
}

/**
 * Resolves the organization in view the same way every member operation does:
 * the staff-picked target, falling back to the caller's own active org for a
 * normal admin who has no picker. Mirroring `targetOrgId ?? activeOrgId` keeps
 * this list and the members roster describing the same organization — two
 * resolutions that could drift would be two different answers on one page.
 */
function defaultGetOrgId(): string | null {
  return (
    useUsersStore.getState().targetOrgId ??
    useAuthStore.getState().activeOrgId
  );
}

export function createExternalAccessStoreCreator(
  client: ExternalAccessApiClient = defaultExternalAccessClient,
  getOrgId: () => string | null = defaultGetOrgId,
): StateCreator<ExternalAccessState> {
  return (set, get) => {
    const asError = (error: unknown): Error =>
      error instanceof Error ? error : new Error(formatErrorMessage(error));

    /**
     * The organization the current contents describe. Held here rather than in
     * state because it is bookkeeping, not something a component renders: what
     * it answers is "are these rows still about the organization in view?".
     */
    let loadedOrgId: string | null = null;

    /**
     * Runs one change against the organization in view, then reloads: the rows
     * that appear are the service's answer, never ones assembled locally from
     * the request.
     *
     * A refusal is recorded AND rethrown. Recording it is what puts the
     * service's own words on the page; rethrowing is what lets the caller tell
     * a refusal from an acceptance — without it a rejected grant resolves
     * exactly like a granted one, and the dialog that asked for it closes on a
     * failure.
     */
    const mutate = async (change: (orgId: string) => Promise<unknown>) => {
      const orgId = getOrgId();
      if (!orgId) {
        return;
      }
      set({ isLoading: true, error: null });
      try {
        await change(orgId);
        set({ isLoading: false });
        await get().load();
      } catch (error) {
        const failure = asError(error);
        set({ error: failure, isLoading: false });
        throw failure;
      }
    };

    return {
      items: [],
      truncated: false,
      // Not permissive by default: an unloaded store shows no controls.
      canManage: false,
      isLoading: false,
      error: null,

      /**
       * Loads the selected organization's external access. With no
       * organization selected there is nothing to ask about, so no call is
       * made — an unscoped request would be a request for the wrong thing.
       *
       * When the organization in view has changed, the previous one's rows are
       * dropped BEFORE the request goes out. They are not a stale version of
       * this answer, they are another organization's answer: rendered under
       * this heading — for the width of the request, or forever if it fails —
       * they would name one tenant's outside accounts as another's.
       */
      load: async () => {
        const orgId = getOrgId();
        if (orgId !== loadedOrgId) {
          loadedOrgId = orgId;
          set({ items: [], truncated: false, canManage: false, error: null });
        }
        if (!orgId) {
          return;
        }
        set({ isLoading: true, error: null });
        try {
          const { items, truncated, canManage } =
            await client.listExternalAccess(orgId);
          // The picker may have moved while this was in flight; a late answer
          // about the organization we left is not an answer about this one.
          if (getOrgId() !== orgId) {
            return;
          }
          set({ items, truncated, canManage, isLoading: false });
        } catch (error) {
          // Whatever is in the list stays — for this organization, that is the
          // last good answer; for a newly selected one, it is already empty.
          // The failure is shown rather than rendered as "nobody has access".
          if (getOrgId() !== orgId) {
            return;
          }
          set({ error: asError(error), isLoading: false });
        }
      },

      grant: (userId: string, role: UserRole) =>
        mutate((orgId) => client.grantExternalAccess(orgId, userId, role)),

      revoke: (userId: string) =>
        mutate((orgId) => client.revokeExternalAccess(orgId, userId)),

      setRole: (userId: string, role: UserRole) =>
        mutate((orgId) => client.setExternalAccessRole(orgId, userId, role)),

      clearError: () => set({ error: null }),
    };
  };
}

export function createExternalAccessStore(
  client?: ExternalAccessApiClient,
  getOrgId?: () => string | null,
) {
  return create<ExternalAccessState>(
    createExternalAccessStoreCreator(client, getOrgId),
  );
}

export const useExternalAccessStore = createExternalAccessStore();
